import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// DesktopIcon.tsx -- a single positioned icon (file or folder) on the canvas.
//
// Per design \u00a76.4 each icon is absolutely positioned at attrs.position {x, y}.
// The canvas (Desktop.tsx) decides which folder is current; this component
// only knows about one entry in that folder.
//
// Behaviours:
//   - Click to select (multi via Cmd/Ctrl+click, range via Shift+click)
//   - Double-click: folder = navigate; document = open preview
//   - HTML5 drag (in-app) starts a useDesktop drag with the entry as payload
//   - Right-click: opens ContextMenu (mounted at Desktop level; this dispatches)
import { useCallback, useRef, useState } from 'react';
import { useDesktop } from '../../hooks/useDesktop.js';
import { formatSize } from '../../utils/format.js';
// Module-level cached transparent 1x1 PNG used to suppress the
// browser's default drag-image. Allocating once per module keeps
// per-drag-start work minimal. The image must be in document
// memory before setDragImage is called or some browsers ignore it;
// the lazy DOM-attach happens on first use.
//
// Why the data: URL: a real <img> with src='/some/path.png' would
// have to round-trip through the network on first drag; the inline
// data URL is decoded synchronously and is part of the JS bundle.
const emptyDragImage = (() => {
    const img = new Image();
    img.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';
    return img;
})();
export function DesktopIcon({ entry, position, onDoubleClick, onContextMenu, onDragStart, onDragEnd, }) {
    const { select, isSelected, startDrag, endDrag, drag, setDragOverTarget } = useDesktop();
    const dragOriginRef = useRef(null);
    const selected = isSelected(entry.itemId);
    // Folder icons act as drop targets when an in-app entry is being
    // dragged. dragOver = "the in-flight drag is currently hovering this
    // folder" -- drives the outline + tint feedback the user expects when
    // hovering a target. Mirrors the trash icon's pattern (TrashIcon.tsx
    // L19-39); both are now consistent.
    const [dragOver, setDragOver] = useState(false);
    const isFolder = entry.type === 'catalog';
    const isSystem = entry.attrs?.system === true;
    const status = entry.attrs?.digestStatus;
    // Is THIS icon the one currently being dragged? Used to dim the
    // source-position icon while the drag-ghost follows the cursor;
    // without this the source icon stays at full opacity and looks
    // like a duplicate. Mirrors macOS Finder + Windows Explorer's
    // "ghost the source" pattern.
    //
    // Reads from the React store (drag.entries) -- which is fine
    // because this is a render-time decision, not an event hot path.
    // The store is committed by the time the next render runs.
    const isDragging = drag?.source === 'in-app'
        && drag.entries?.[0]?.itemId === entry.itemId;
    const onClick = useCallback((e) => {
        e.stopPropagation();
        select(entry, e.metaKey || e.ctrlKey, e.shiftKey);
    }, [entry, select]);
    const onDouble = useCallback((e) => {
        e.stopPropagation();
        onDoubleClick?.(entry);
    }, [entry, onDoubleClick]);
    const onContext = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        onContextMenu?.(entry, e.clientX, e.clientY);
    }, [entry, onContextMenu]);
    // Native HTML5 drag for in-app moves. We register the drag with useDesktop
    // so drop targets (TrashIcon, other DesktopIcons of folder type) can read
    // the payload during dragover. dataTransfer also gets the entry id so
    // cross-window drops work too.
    //
    // Drag image: the browser's default drag image is a screenshot of the
    // dragged element which captures the full panel chrome (the "square
    // background around the icon" the user reported). Suppressing the
    // default with setDragImage(transparent) lets the existing dragGhost
    // state in Desktop.tsx (L160-162) own the visual -- a clean
    // follow-cursor preview without the chrome-screenshot.
    const onNativeDragStart = useCallback((e) => {
        dragOriginRef.current = { x: e.clientX, y: e.clientY };
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('application/x-fdn-entry', JSON.stringify(entry));
        e.dataTransfer.setDragImage(emptyDragImage, 0, 0);
        startDrag({ source: 'in-app', entries: [entry] });
        onDragStart?.(entry, e);
    }, [entry, startDrag, onDragStart]);
    // No per-tick `drag` handler. We deliberately do not subscribe to
    // the `onDrag` event because any state update on every tick (~60 Hz)
    // forces React to re-render mid-drag, and a DOM tree mutation
    // during an active HTML5 drag aborts the drag in Chromium. The
    // dragOriginRef field is still tracked for reference symmetry but
    // never read by a handler that mutates state.
    const onNativeDragEnd = useCallback((e) => {
        dragOriginRef.current = null;
        setDragOver(false);
        endDrag();
        onDragEnd?.(entry, e);
    }, [entry, endDrag, onDragEnd]);
    // Drop-target feedback for folder icons. Mirrors TrashIcon's pattern
    // so the user gets the same outline-on-hover signal whether they're
    // dragging into trash or into a regular folder.
    //
    // CRITICAL: the eligibility check is purely on dataTransfer.types,
    // NOT on React store state. Why: the React state ('drag' from
    // useDesktop) is updated by onDragStart -> startDrag, which runs
    // through React's setState batching and is NOT visible during the
    // synchronous dragenter/dragover events on a sibling element if
    // those fire in the same task tick. dataTransfer.types is set
    // synchronously by setData('application/x-fdn-entry', ...) and is
    // always visible in subsequent drag events. Same approach the
    // global DragOverlay uses for native file drops (Files type
    // sniffing).
    //
    // Self-drop guard: also block dropping a folder onto itself, by
    // comparing the dragged source's data-item-id (read from the
    // dragstart-painted dataTransfer.types presence; we'd need the
    // actual id to compare, but dataTransfer.getData() returns ''
    // outside drop on most browsers). Easier path: bail in the
    // canvas's drop handler -- it already skips when
    // folderTarget.getAttribute === entry.itemId (Desktop.tsx:136).
    // Visual self-drop highlight is harmless; the drop just no-ops.
    //
    // Pattern: dragenter/dragover preventDefault to declare "yes, this
    // element accepts this drop"; setting dropEffect tells the cursor
    // which kind of drop is happening. Without preventDefault on
    // dragover the browser refuses the drop and the cursor stays as
    // not-allowed -- a primary cause of "the folder doesn't sense it"
    // feedback gap.
    const isInAppDrag = (e) => {
        if (!isFolder)
            return false;
        return Array.from(e.dataTransfer?.types ?? [])
            .includes('application/x-fdn-entry');
    };
    const onFolderDragEnter = useCallback((e) => {
        if (!isInAppDrag(e))
            return;
        e.preventDefault();
        e.stopPropagation();
        setDragOver(true);
        setDragOverTarget(entry.itemId);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- isInAppDrag is a stable closure over isFolder; inlining would clutter
    }, [entry.itemId, setDragOverTarget, isFolder]);
    const onFolderDragOver = useCallback((e) => {
        if (!isInAppDrag(e))
            return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        // eslint-disable-next-line react-hooks/exhaustive-deps -- see onFolderDragEnter
    }, [isFolder]);
    const onFolderDragLeave = useCallback((e) => {
        // dragleave fires when crossing into a CHILD element too (the
        // .icon-glyph or .icon-name span). Without a relatedTarget check
        // the outline flickers off-and-on as the cursor moves over the
        // glyph. relatedTarget is null when leaving the icon entirely;
        // a descendant otherwise.
        const next = e.relatedTarget;
        if (next && e.currentTarget.contains(next))
            return;
        setDragOver(false);
        setDragOverTarget(null);
    }, [setDragOverTarget]);
    // The actual drop is handled at the canvas level via the closest()
    // walk on data-drop-folder-id (Desktop.tsx). Per-icon onDrop is
    // redundant for the move logic itself; we just clear the visual
    // feedback when the drop arrives so the outline doesn't linger.
    // Do NOT preventDefault or stopPropagation here -- letting the
    // event bubble to the canvas is exactly how the move gets executed.
    const onFolderDrop = useCallback(() => {
        setDragOver(false);
        setDragOverTarget(null);
    }, [setDragOverTarget]);
    return (_jsxs("div", { className: `desktop-icon ${selected ? 'selected' : ''} ${isFolder ? 'folder' : 'file'} ${isSystem ? 'system' : ''} ${dragOver ? 'drag-over' : ''} ${isDragging ? 'dragging' : ''}`, style: { left: position.x, top: position.y }, onClick: onClick, onDoubleClick: onDouble, onContextMenu: onContext, draggable: true, onDragStart: onNativeDragStart, onDragEnd: onNativeDragEnd, "data-item-id": entry.itemId, "data-item-type": entry.type, ...(isFolder ? {
            'data-drop-folder-id': entry.itemId,
            onDragEnter: onFolderDragEnter,
            onDragOver: onFolderDragOver,
            onDragLeave: onFolderDragLeave,
            onDrop: onFolderDrop,
        } : {}), children: [_jsx("div", { className: "icon-glyph", "aria-hidden": true, children: isFolder
                    ? (isSystem ? systemGlyph(entry.displayName ?? '') : '\uD83D\uDCC1' /* folder */)
                    : fileGlyph(entry.attrs?.contentType) }), _jsx("div", { className: "icon-name", title: entry.displayName ?? entry.itemId, children: entry.displayName ?? entry.itemId.slice(0, 8) }), entry.attrs?.fileSize !== undefined && (_jsx("div", { className: "icon-meta", children: formatSize(Number(entry.attrs.fileSize)) })), status && status !== 'ready' && (_jsx("div", { className: `icon-status ${status}`, children: status === 'uploading' ? 'uploading\u2026'
                    : status === 'extracting' ? 'extracting\u2026'
                        : status === 'chunking' ? 'chunking\u2026'
                            : status === 'embedding' ? 'embedding\u2026'
                                : status === 'failed' ? 'failed' : '' }))] }));
}
function fileGlyph(contentType) {
    if (!contentType)
        return '\uD83D\uDCC4'; // generic page
    if (contentType.startsWith('image/'))
        return '\uD83D\uDDBC\uFE0F';
    if (contentType === 'application/pdf')
        return '\uD83D\uDCD5';
    if (contentType.startsWith('text/'))
        return '\uD83D\uDCDD';
    if (contentType.includes('spreadsheet') || contentType.includes('excel'))
        return '\uD83D\uDCCA';
    if (contentType.includes('presentation') || contentType.includes('powerpoint'))
        return '\uD83D\uDCCA';
    if (contentType.includes('word') || contentType.includes('msword'))
        return '\uD83D\uDCC4';
    if (contentType.includes('zip'))
        return '\uD83D\uDCE6';
    return '\uD83D\uDCC4';
}
function systemGlyph(name) {
    switch (name.toLowerCase()) {
        case 'aidocs': return '\u2728'; // sparkles
        case 'archive': return '\uD83D\uDCE6'; // box
        case 'trash': return '\uD83D\uDDD1\uFE0F'; // wastebasket
        default: return '\uD83C\uDFE0'; // home (root)
    }
}
